import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palette, Save, RotateCcw } from "lucide-react";

const DEFAULTS = {
  primary: "#CF0283",
  accent: "#F59E0B",
  org_name: "Shopping Salou",
};

export default function AdminSettings() {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm({ ...DEFAULTS, ...data }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        primary: form.primary,
        accent: form.accent,
        org_name: form.org_name,
      });
      toast.success("Configuració desada");
      // Notify app to refresh theme
      window.dispatchEvent(new Event("ss-settings-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const reset = () => setForm(DEFAULTS);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <div className="overline text-accent">Marca</div>
        <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Configuració</h1>
        <p className="text-sm text-muted-foreground mt-2">Personalitza els colors corporatius i el nom de l'organització. Els canvis s'apliquen a l'instant.</p>
      </div>

      <Card className="rounded-2xl border-2">
        <CardContent className="p-6 sm:p-8 space-y-6">
          <div>
            <Label>Nom de l'organització</Label>
            <Input className="mt-2 rounded-xl" value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="settings-org-name" />
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label>Color primari</Label>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={form.primary} onChange={(e) => setForm({ ...form, primary: e.target.value })} className="w-14 h-14 rounded-xl border-2 border-border cursor-pointer" data-testid="settings-primary-picker" />
                <Input value={form.primary} onChange={(e) => setForm({ ...form, primary: e.target.value })} className="rounded-xl font-mono" data-testid="settings-primary-input" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Utilitzat a botons, headers i accents principals.</p>
            </div>
            <div>
              <Label>Color accent</Label>
              <div className="mt-2 flex items-center gap-3">
                <input type="color" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="w-14 h-14 rounded-xl border-2 border-border cursor-pointer" data-testid="settings-accent-picker" />
                <Input value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} className="rounded-xl font-mono" data-testid="settings-accent-input" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Petites accentuacions (overlines, badges, hover).</p>
            </div>
          </div>

          <div>
            <Label>Vista prèvia</Label>
            <div className="mt-2 rounded-2xl p-6 border-2 border-border" style={{ background: form.primary, color: "#fff" }}>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: form.accent }}>{form.org_name}</div>
              <div className="font-heading font-bold text-2xl mt-2">Aquest és el color principal</div>
              <button className="mt-4 rounded-full px-5 py-2 text-sm font-semibold" style={{ background: form.accent, color: form.primary }}>Botó accent</button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="settings-save-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Desant…" : "Desar canvis"}</Button>
            <Button variant="outline" onClick={reset} className="rounded-full" data-testid="settings-reset-btn"><RotateCcw className="w-4 h-4 mr-2" /> Restaurar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
